import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, API } from "../api";
import { useAuth } from "../AuthContext";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  FileText, ChevronLeft, ChevronRight, Upload, Download, Play, CheckCircle2,
  Clock, AlertCircle, Package, MessageCircle, Trash2, Plus, UserCheck,
} from "lucide-react";
import { toast } from "sonner";

const POLL_MS = 30000;

const STATUS_META = {
  pending:    { label: "Pending",    color: "slate",   border: "border-l-slate-500",   icon: Clock },
  processing: { label: "Processing", color: "blue",    border: "border-l-blue-500",    icon: Play },
  ready:      { label: "Ready",      color: "amber",   border: "border-l-amber-500",   icon: Package },
  retrieved:  { label: "Retrieved",  color: "emerald", border: "border-l-emerald-500", icon: CheckCircle2 },
};

const STATUS_PILL = {
  pending:    "bg-slate-500/15 text-slate-300 border-slate-500/30",
  processing: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  ready:      "bg-amber-500/15 text-amber-300 border-amber-500/30",
  retrieved:  "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
};

const TABS = [
  { key: "all",        label: "All" },
  { key: "pending",    label: "Pending" },
  { key: "processing", label: "Processing" },
  { key: "ready",      label: "Ready" },
  { key: "retrieved",  label: "Retrieved" },
];

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function shiftMonth(month, delta) {
  const [y, m] = month.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(month) {
  const [y, m] = month.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleString([], { month: "long", year: "numeric" });
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" });
  } catch { return "—"; }
}

function fmtMoney(v) {
  if (v == null) return "—";
  const n = Number(v);
  if (!isFinite(n)) return "—";
  return `RM ${n.toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ─── PAGE ────────────────────────────────────────────────────────────────────

export default function Invoice() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [month, setMonth] = useState(currentMonth());
  const [tab, setTab] = useState("all");
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState({ counts: {}, total: 0 });
  const [clients, setClients] = useState([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [companyMgrOpen, setCompanyMgrOpen] = useState(false);
  const [activeInvoice, setActiveInvoice] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { month };
      if (tab !== "all") params.status = tab;
      const [list, sum, cli] = await Promise.all([
        api.get("/invoices", { params }),
        api.get("/invoices/summary", { params: { month } }),
        api.get("/invoices/clients"),
      ]);
      setInvoices(list.data || []);
      setSummary(sum.data || { counts: {}, total: 0 });
      setClients(cli.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load invoices");
    } finally {
      setLoading(false);
    }
  }, [month, tab]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  const canUploadForAny = useMemo(
    () => clients.some((c) => c.can_upload),
    [clients],
  );
  const scopedLabel = useMemo(() => {
    if (isAdmin) return "Showing all clients";
    if (user?.invoice_retriever) return "Retriever: read-access to all clients";
    const names = clients.map((c) => c.name).join(", ") || "none";
    return `Scoped to: ${names}`;
  }, [isAdmin, user, clients]);

  const counts = summary.counts || {};

  return (
    <Layout title="Invoice">
      <div className="space-y-4" data-testid="invoice-page">
        {/* TOP BAR: title + month picker */}
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-xl font-semibold tracking-tight flex items-center gap-2">
            <FileText className="w-5 h-5 text-yellow-400" /> Invoice
          </h1>
          <div className="flex-1" />
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" className="border-slate-700 h-8 w-8"
              onClick={() => setMonth(shiftMonth(month, -1))}
              data-testid="invoice-month-prev">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="text-sm font-medium num px-3 h-8 flex items-center rounded border hairline surface-1 min-w-[140px] justify-center"
              data-testid="invoice-month-label">
              {monthLabel(month)}
            </div>
            <Button variant="outline" size="icon" className="border-slate-700 h-8 w-8"
              onClick={() => setMonth(shiftMonth(month, 1))}
              data-testid="invoice-month-next">
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Input type="month" value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="bg-[#0a0e1a] border-slate-800 h-8 w-[140px] num text-xs ml-1"
              data-testid="invoice-month-input" />
          </div>
        </div>

        {/* TABS */}
        <div className="flex items-center gap-1 overflow-x-auto scrollbar-thin">
          {TABS.map((t) => {
            const n = t.key === "all" ? (summary.total || 0) : (counts[t.key] || 0);
            const active = tab === t.key;
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                data-testid={`invoice-tab-${t.key}`}
                className={`text-xs px-3 py-1.5 rounded-full border whitespace-nowrap transition ${active
                  ? "bg-blue-500/15 border-blue-500/60 text-blue-200"
                  : "border-slate-800 hover:border-slate-600 surface-1 text-slate-300"}`}>
                {t.label}
                <span className="ml-1.5 num text-[10px] opacity-80">{n}</span>
              </button>
            );
          })}
        </div>

        {/* ACTIONS ROW */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-xs text-slate-500 flex-1 min-w-0 truncate" data-testid="invoice-scope-label">
            {scopedLabel}
          </div>
          {isAdmin && (
            <Button variant="outline" className="border-slate-700 h-9"
              onClick={() => setCompanyMgrOpen(true)}
              data-testid="invoice-manage-companies-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Manage clients
            </Button>
          )}
          {canUploadForAny && (
            <Button className="bg-blue-600 hover:bg-blue-500 h-9"
              onClick={() => setUploadOpen(true)}
              data-testid="invoice-upload-btn">
              <Upload className="w-4 h-4 mr-1.5" /> Upload timesheet
            </Button>
          )}
        </div>

        {/* EMPTY STATE */}
        {!loading && invoices.length === 0 && (
          <div className="rounded-lg border hairline surface-1 p-10 text-center">
            <FileText className="w-8 h-8 text-slate-600 mx-auto mb-2" />
            <div className="text-sm text-slate-300">
              No {tab === "all" ? "" : tab + " "}invoices for {monthLabel(month)}.
            </div>
            {canUploadForAny && tab === "all" && (
              <div className="text-xs text-slate-500 mt-1">
                Click <b>Upload timesheet</b> to file one for this month.
              </div>
            )}
          </div>
        )}

        {/* CARD GRID */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="invoice-card-grid">
          {invoices.map((inv) => (
            <InvoiceCard key={inv.id} inv={inv}
              isAdmin={isAdmin}
              user={user}
              onOpen={() => setActiveInvoice(inv)}
              onRefresh={load} />
          ))}
        </div>
      </div>

      <UploadTimesheetsDialog open={uploadOpen} onOpenChange={setUploadOpen}
        month={month} clients={clients.filter((c) => c.can_upload)}
        onDone={() => { load(); }} />

      {isAdmin && (
        <CompanyManagerDialog open={companyMgrOpen} onOpenChange={setCompanyMgrOpen}
          onDone={load} />
      )}

      {activeInvoice && (
        <InvoiceDetailDialog invoiceId={activeInvoice.id}
          onOpenChange={(v) => !v && setActiveInvoice(null)}
          isAdmin={isAdmin} user={user}
          onRefresh={load} />
      )}
    </Layout>
  );
}

// ─── CARD ────────────────────────────────────────────────────────────────────

function InvoiceCard({ inv, isAdmin, user, onOpen, onRefresh }) {
  const meta = STATUS_META[inv.status] || STATUS_META.pending;
  const Icon = meta.icon;
  const timesheet = (inv.files || []).find((f) => f.file_type === "timesheet");
  const packCount = (inv.files || []).filter((f) => f.file_type !== "timesheet").length;

  const notifyAdminWA = (e) => {
    e.stopPropagation();
    const phone = "";  // wa.me/?phone empty → contact picker
    const text = `Timesheet uploaded for ${inv.client_name} – ${inv.month}. Please process.`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  };
  const notifyStaffWA = (e) => {
    e.stopPropagation();
    const text = `Invoice pack ready for ${inv.client_name} – ${inv.month}. Please retrieve.`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  };

  return (
    <div
      className={`rounded-lg border-l-4 ${meta.border} border hairline surface-1 p-3 flex flex-col gap-2 cursor-pointer hover:border-blue-500/40 transition`}
      onClick={onOpen}
      data-testid={`invoice-card-${inv.id}`}
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold truncate">{inv.client_name}</div>
          <div className="text-[11px] text-slate-500 num">{inv.month}</div>
        </div>
        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border num ${STATUS_PILL[inv.status]}`}>
          <Icon className="w-3 h-3 inline mr-0.5" /> {meta.label}
        </span>
      </div>

      {(inv.invoice_number || inv.invoice_total != null) && (
        <div className="text-xs text-slate-300 flex items-center gap-2">
          {inv.invoice_number && <span className="num">#{inv.invoice_number}</span>}
          {inv.invoice_total != null && <span className="num text-emerald-300">{fmtMoney(inv.invoice_total)}</span>}
        </div>
      )}

      <div className="text-[11px] text-slate-500 space-y-0.5 num">
        <div>Submitted by <b className="text-slate-300">{inv.submitted_by_name}</b> · {fmtDate(inv.submitted_at)}</div>
        {inv.retrieved_at && (
          <div>Retrieved by <b className="text-slate-300">{inv.retrieved_by_name}</b> · {fmtDate(inv.retrieved_at)}</div>
        )}
      </div>

      {(timesheet || packCount > 0) && (
        <div className="flex items-center gap-1 flex-wrap">
          {timesheet && (
            <FileChip file={timesheet} inv={inv} variant="ts" />
          )}
          {(inv.files || []).filter((f) => f.file_type !== "timesheet").map((f) => (
            <FileChip key={f.id} file={f} inv={inv} variant="pack" />
          ))}
        </div>
      )}

      {/* ACTION BUTTONS */}
      <div className="flex items-center gap-2 pt-1 flex-wrap">
        {isAdmin && inv.status === "pending" && (
          <Button size="sm" className="bg-blue-600 hover:bg-blue-500 h-7 text-xs"
            onClick={(e) => { e.stopPropagation(); onOpen(); }}
            data-testid={`invoice-start-btn-${inv.id}`}>
            <Play className="w-3 h-3 mr-1" /> Start processing
          </Button>
        )}
        {isAdmin && ["pending", "processing"].includes(inv.status) && (
          <Button size="sm" className="bg-amber-600 hover:bg-amber-500 h-7 text-xs"
            onClick={(e) => { e.stopPropagation(); onOpen(); }}
            data-testid={`invoice-pack-btn-${inv.id}`}>
            <Package className="w-3 h-3 mr-1" /> Upload pack
          </Button>
        )}
        {inv.status === "ready" && (
          <RetrieveZipButton inv={inv} onRefresh={onRefresh} />
        )}
        {inv.status === "retrieved" && (
          <RetrieveZipButton inv={inv} onRefresh={onRefresh} label="Re-download" />
        )}

        {/* WhatsApp notify buttons */}
        {inv.status === "pending" && (
          <Button size="sm" variant="outline"
            className="h-7 text-xs border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
            onClick={notifyAdminWA} data-testid={`invoice-wa-admin-${inv.id}`}>
            <MessageCircle className="w-3 h-3 mr-1" /> Notify admin
          </Button>
        )}
        {isAdmin && inv.status === "ready" && (
          <Button size="sm" variant="outline"
            className="h-7 text-xs border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
            onClick={notifyStaffWA} data-testid={`invoice-wa-staff-${inv.id}`}>
            <MessageCircle className="w-3 h-3 mr-1" /> Notify {inv.submitted_by_name}
          </Button>
        )}
      </div>
    </div>
  );
}

function FileChip({ file, inv, variant }) {
  const url = `${API}/invoices/${inv.id}/files/${file.id}`;
  const label = variant === "ts" ? "Timesheet"
    : (file.file_type === "invoice_pdf" ? "Invoice PDF"
      : file.file_type === "breakdown_xlsx" ? "Breakdown XLSX"
      : "Extra");
  const tone = variant === "ts"
    ? "bg-slate-500/10 border-slate-500/30 text-slate-300"
    : "bg-amber-500/10 border-amber-500/30 text-amber-200";
  const download = async (e) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem("mercy_token");
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) { toast.error("Download failed"); return; }
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = file.file_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } catch {
      toast.error("Download failed");
    }
  };
  return (
    <button onClick={download}
      data-testid={`invoice-file-chip-${file.id}`}
      className={`text-[10px] px-2 py-1 rounded border num hover:brightness-125 ${tone}`}>
      <Download className="w-3 h-3 inline mr-1" /> {label}
    </button>
  );
}

function RetrieveZipButton({ inv, onRefresh, label }) {
  const [busy, setBusy] = useState(false);
  const doRetrieve = async (e) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/invoices/${inv.id}/retrieve`);
      const token = localStorage.getItem("mercy_token");
      const resp = await fetch(`${API}/invoices/${inv.id}/zip`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) { toast.error("Zip download failed"); setBusy(false); return; }
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      const safe = (inv.client_name || "Invoice").replace(/\s+/g, "_");
      a.download = `${safe}_${inv.month}_pack.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
      toast.success("Retrieved invoice pack");
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Retrieve failed");
    } finally { setBusy(false); }
  };
  return (
    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500 h-7 text-xs"
      onClick={doRetrieve} disabled={busy}
      data-testid={`invoice-retrieve-btn-${inv.id}`}>
      <Download className="w-3 h-3 mr-1" /> {label || "Retrieve all"}
    </Button>
  );
}

// ─── UPLOAD TIMESHEETS (batch) ───────────────────────────────────────────────

function UploadTimesheetsDialog({ open, onOpenChange, month, clients, onDone }) {
  const empty = () => ({ client_id: "", file_name: "", content_b64: "", notes: "" });
  const [rows, setRows] = useState([empty()]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setRows([empty()]);
  }, [open]);

  const pickFile = async (idx, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Max file size 10 MB"); return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const b64 = String(e.target.result);
      setRows((prev) => prev.map((r, i) => i === idx
        ? { ...r, file_name: file.name, content_b64: b64 } : r));
    };
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    const valid = rows.every((r) => r.client_id && r.content_b64);
    if (!valid) { toast.error("Fill a client and file for every row"); return; }
    setBusy(true);
    try {
      const body = {
        month,
        rows: rows.map((r) => ({
          client_id: r.client_id,
          file_name: r.file_name,
          content_b64: r.content_b64,
          notes: r.notes || undefined,
        })),
      };
      const { data } = await api.post("/invoices/batch", body);
      toast.success(`Created ${data.count} invoice${data.count === 1 ? "" : "s"}`);
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-3xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Upload timesheets — {monthLabel(month)}</DialogTitle>
        </DialogHeader>
        <div className="space-y-2 flex-1 overflow-y-auto" data-testid="invoice-upload-rows">
          {rows.map((r, idx) => (
            <div key={idx} className="rounded-md border hairline p-3 grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2">
              <div>
                <Label className="text-xs text-slate-400">Client</Label>
                <Select value={r.client_id}
                  onValueChange={(v) => setRows((prev) => prev.map((x, i) => i === idx ? { ...x, client_id: v } : x))}>
                  <SelectTrigger className="bg-[#0a0e1a] border-slate-800 mt-1 h-9"
                    data-testid={`invoice-upload-client-${idx}`}>
                    <SelectValue placeholder="Pick client" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[280px]">
                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-400">Timesheet file</Label>
                <div className="mt-1">
                  <input type="file"
                    accept=".pdf,.xlsx,.xls,.csv,.png,.jpg,.jpeg,.txt"
                    onChange={(e) => pickFile(idx, e.target.files?.[0])}
                    className="block w-full text-xs text-slate-300 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-blue-600 file:text-white file:cursor-pointer hover:file:bg-blue-500"
                    data-testid={`invoice-upload-file-${idx}`} />
                  {r.file_name && <div className="text-[10px] text-slate-500 mt-1 truncate">📄 {r.file_name}</div>}
                </div>
              </div>
              <div className="flex items-end">
                <Button variant="ghost" size="sm"
                  onClick={() => setRows((prev) => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev)}
                  disabled={rows.length === 1}
                  className="h-9 text-red-300 hover:text-red-200 hover:bg-red-500/10"
                  data-testid={`invoice-upload-row-remove-${idx}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
              <div className="md:col-span-3">
                <Label className="text-xs text-slate-400">Notes (optional)</Label>
                <Textarea value={r.notes}
                  onChange={(e) => setRows((prev) => prev.map((x, i) => i === idx ? { ...x, notes: e.target.value } : x))}
                  className="bg-[#0a0e1a] border-slate-800 mt-1 min-h-[40px] text-xs"
                  placeholder="Anything admin should know…" />
              </div>
            </div>
          ))}
          <Button variant="outline" className="border-slate-700 w-full"
            onClick={() => setRows((prev) => [...prev, empty()])}
            data-testid="invoice-upload-add-row">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add another client
          </Button>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-blue-600 hover:bg-blue-500"
            data-testid="invoice-upload-submit">
            <Upload className="w-4 h-4 mr-1.5" /> Submit {rows.length} timesheet{rows.length === 1 ? "" : "s"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── DETAIL DIALOG (admin actions) ───────────────────────────────────────────

function InvoiceDetailDialog({ invoiceId, onOpenChange, isAdmin, user, onRefresh }) {
  const [inv, setInv] = useState(null);
  const [packOpen, setPackOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [overrideStatus, setOverrideStatus] = useState("");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(`/invoices/${invoiceId}`);
      setInv(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
      onOpenChange(false);
    }
  }, [invoiceId, onOpenChange]);

  useEffect(() => { refresh(); }, [refresh]);

  const doStart = async () => {
    setBusy(true);
    try {
      await api.post(`/invoices/${invoiceId}/start`);
      toast.success("Processing started");
      onRefresh?.(); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const doOverride = async (newStatus) => {
    setBusy(true);
    try {
      await api.patch(`/invoices/${invoiceId}/status`, { status: newStatus });
      toast.success(`Status set to ${newStatus}`);
      onRefresh?.(); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); setOverrideStatus(""); }
  };

  if (!inv) {
    return (
      <Dialog open onOpenChange={onOpenChange}>
        <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-2xl">
          <DialogTitle>Loading invoice…</DialogTitle>
        </DialogContent>
      </Dialog>
    );
  }

  const meta = STATUS_META[inv.status] || STATUS_META.pending;
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {inv.client_name} · {inv.month}
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border num ${STATUS_PILL[inv.status]}`}>
              {meta.label}
            </span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 flex-1 overflow-y-auto text-sm">
          <Row label="Submitted" value={`${inv.submitted_by_name} · ${fmtDate(inv.submitted_at)}`} />
          {inv.processed_at && <Row label="Processing started" value={`${inv.processed_by_name || "—"} · ${fmtDate(inv.processed_at)}`} />}
          {inv.ready_at && <Row label="Pack ready" value={`${inv.ready_by_name || "—"} · ${fmtDate(inv.ready_at)}`} />}
          {inv.retrieved_at && <Row label="Retrieved" value={`${inv.retrieved_by_name || "—"} · ${fmtDate(inv.retrieved_at)}`} />}
          {inv.invoice_number && <Row label="Invoice #" value={inv.invoice_number} />}
          {inv.invoice_total != null && <Row label="Total" value={fmtMoney(inv.invoice_total)} />}
          {inv.notes && <Row label="Notes" value={inv.notes} mono />}

          <div>
            <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Files</div>
            {(inv.files || []).length === 0 ? (
              <div className="text-xs text-slate-500">No files yet.</div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {(inv.files || []).map((f) => (
                  <FileChip key={f.id} file={f} inv={inv}
                    variant={f.file_type === "timesheet" ? "ts" : "pack"} />
                ))}
              </div>
            )}
          </div>

          {/* Admin actions */}
          {isAdmin && (
            <div className="pt-3 border-t hairline space-y-2">
              <div className="flex flex-wrap gap-2">
                {inv.status === "pending" && (
                  <Button onClick={doStart} disabled={busy} className="bg-blue-600 hover:bg-blue-500"
                    data-testid="invoice-detail-start">
                    <Play className="w-4 h-4 mr-1.5" /> Start processing
                  </Button>
                )}
                {["pending", "processing"].includes(inv.status) && (
                  <Button onClick={() => setPackOpen(true)} className="bg-amber-600 hover:bg-amber-500"
                    data-testid="invoice-detail-upload-pack">
                    <Package className="w-4 h-4 mr-1.5" /> Upload invoice pack
                  </Button>
                )}
                {inv.status === "ready" && (
                  <Button onClick={() => doOverride("retrieved")} disabled={busy}
                    variant="outline" className="border-slate-700"
                    data-testid="invoice-detail-mark-retrieved">
                    <UserCheck className="w-4 h-4 mr-1.5" /> Mark retrieved
                  </Button>
                )}
                {inv.status === "retrieved" && (
                  <Button onClick={() => doOverride("ready")} disabled={busy}
                    variant="outline" className="border-slate-700"
                    data-testid="invoice-detail-mark-ready">
                    <AlertCircle className="w-4 h-4 mr-1.5" /> Send back to ready
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
      {packOpen && (
        <UploadPackDialog invoiceId={invoiceId} currentInv={inv}
          onOpenChange={(v) => { setPackOpen(v); if (!v) { refresh(); onRefresh?.(); } }} />
      )}
    </Dialog>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-2 text-sm">
      <div className="text-xs text-slate-500 uppercase tracking-wider pt-0.5">{label}</div>
      <div className={mono ? "font-mono text-xs whitespace-pre-wrap" : ""}>{value}</div>
    </div>
  );
}

// ─── UPLOAD PACK (admin) ─────────────────────────────────────────────────────

function UploadPackDialog({ invoiceId, currentInv, onOpenChange }) {
  const [form, setForm] = useState({
    invoice_pdf: null,  // { file_name, content_b64 }
    breakdown_xlsx: null,
    extras: [],
    invoice_number: currentInv?.invoice_number || "",
    invoice_total: currentInv?.invoice_total != null ? String(currentInv.invoice_total) : "",
    notes: currentInv?.notes || "",
  });
  const [busy, setBusy] = useState(false);

  const readFile = (file) => new Promise((resolve, reject) => {
    if (!file) return resolve(null);
    if (file.size > 10 * 1024 * 1024) {
      reject(new Error("File exceeds 10 MB")); return;
    }
    const r = new FileReader();
    r.onload = (e) => resolve({ file_name: file.name, content_b64: String(e.target.result) });
    r.onerror = reject;
    r.readAsDataURL(file);
  });

  const pickSlot = async (slot, file) => {
    try {
      const payload = await readFile(file);
      setForm((f) => ({ ...f, [slot]: payload }));
    } catch (e) { toast.error(e.message); }
  };
  const addExtra = async (file) => {
    try {
      const payload = await readFile(file);
      if (payload) setForm((f) => ({ ...f, extras: [...f.extras, payload] }));
    } catch (e) { toast.error(e.message); }
  };
  const removeExtra = (idx) => {
    setForm((f) => ({ ...f, extras: f.extras.filter((_, i) => i !== idx) }));
  };

  const submit = async () => {
    if (!form.invoice_pdf) {
      toast.error("Invoice PDF is required"); return;
    }
    setBusy(true);
    try {
      const files = [
        { file_type: "invoice_pdf", file_name: form.invoice_pdf.file_name, content_b64: form.invoice_pdf.content_b64 },
      ];
      if (form.breakdown_xlsx) files.push({
        file_type: "breakdown_xlsx", file_name: form.breakdown_xlsx.file_name,
        content_b64: form.breakdown_xlsx.content_b64,
      });
      form.extras.forEach((x) => files.push({
        file_type: "extra", file_name: x.file_name, content_b64: x.content_b64,
      }));
      const body = {
        files,
        invoice_number: form.invoice_number || null,
        invoice_total: form.invoice_total === "" ? null : Number(form.invoice_total),
        notes: form.notes || null,
      };
      await api.post(`/invoices/${invoiceId}/pack`, body);
      toast.success("Invoice pack uploaded — status is now Ready");
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-4 h-4 text-amber-400" /> Upload invoice pack
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 flex-1 overflow-y-auto">
          <div>
            <Label className="text-xs text-slate-400">Invoice PDF <span className="text-red-400">*</span></Label>
            <input type="file" accept=".pdf"
              onChange={(e) => pickSlot("invoice_pdf", e.target.files?.[0])}
              className="block w-full mt-1 text-xs text-slate-300 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-blue-600 file:text-white file:cursor-pointer"
              data-testid="invoice-pack-pdf" />
            {form.invoice_pdf && <div className="text-[10px] text-slate-500 mt-1">📄 {form.invoice_pdf.file_name}</div>}
          </div>
          <div>
            <Label className="text-xs text-slate-400">Breakdown XLSX (optional)</Label>
            <input type="file" accept=".xlsx,.xls,.csv"
              onChange={(e) => pickSlot("breakdown_xlsx", e.target.files?.[0])}
              className="block w-full mt-1 text-xs text-slate-300 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-slate-700 file:text-white file:cursor-pointer"
              data-testid="invoice-pack-xlsx" />
            {form.breakdown_xlsx && <div className="text-[10px] text-slate-500 mt-1">📊 {form.breakdown_xlsx.file_name}</div>}
          </div>
          <div>
            <Label className="text-xs text-slate-400">Extras (optional, multiple)</Label>
            <input type="file" multiple
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                files.forEach(addExtra);
                e.target.value = "";
              }}
              className="block w-full mt-1 text-xs text-slate-300 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-slate-700 file:text-white file:cursor-pointer"
              data-testid="invoice-pack-extras" />
            {form.extras.length > 0 && (
              <div className="mt-1 space-y-0.5">
                {form.extras.map((x, i) => (
                  <div key={i} className="text-[10px] text-slate-500 flex items-center gap-1">
                    <span className="flex-1 truncate">📎 {x.file_name}</span>
                    <button onClick={() => removeExtra(i)} className="text-red-400 hover:text-red-300">✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2 pt-2">
            <div>
              <Label className="text-xs text-slate-400">Invoice #</Label>
              <Input value={form.invoice_number}
                onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
                className="bg-[#0a0e1a] border-slate-800 mt-1 num text-xs"
                placeholder="INV-2026-001"
                data-testid="invoice-pack-number" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Total (RM)</Label>
              <Input type="number" step="0.01" value={form.invoice_total}
                onChange={(e) => setForm({ ...form, invoice_total: e.target.value })}
                className="bg-[#0a0e1a] border-slate-800 mt-1 num text-xs"
                placeholder="1234.50"
                data-testid="invoice-pack-total" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-400">Notes (optional)</Label>
            <Textarea value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="bg-[#0a0e1a] border-slate-800 mt-1 min-h-[60px] text-xs"
              data-testid="invoice-pack-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !form.invoice_pdf}
            className="bg-amber-600 hover:bg-amber-500"
            data-testid="invoice-pack-submit">
            <Upload className="w-4 h-4 mr-1.5" /> Upload & mark ready
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── CLIENT MANAGER (admin) ──────────────────────────────────────────────────

function CompanyManagerDialog({ open, onOpenChange, onDone }) {
  const [rows, setRows] = useState([]);
  const [name, setName] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/invoices/clients");
      setRows(data || []);
    } catch { setRows([]); }
  }, []);

  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  const add = async () => {
    const n = name.trim();
    if (!n) return;
    setBusy(true);
    try {
      await api.post("/invoices/clients", { name: n });
      toast.success(`Added ${n}`);
      setName("");
      refresh();
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/invoices/clients/${deleteTarget.id}`);
      toast.success(`Removed ${deleteTarget.name}`);
      setDeleteTarget(null);
      refresh(); onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-lg max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Invoice clients</DialogTitle>
          </DialogHeader>
          <div className="text-xs text-slate-500">
            These are the factories you can file invoices against. They are shared with the Kedatangan roster — adding or removing here affects both modules.
          </div>
          <div className="flex gap-2">
            <Input value={name} onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") add(); }}
              placeholder="New client name…"
              className="bg-[#0a0e1a] border-slate-800 h-9"
              data-testid="invoice-new-client-name" />
            <Button onClick={add} disabled={busy || !name.trim()} className="bg-blue-600 hover:bg-blue-500"
              data-testid="invoice-add-client-btn">
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto rounded border hairline surface-2 divide-y divide-slate-900/70">
            {rows.length === 0 && (
              <div className="text-xs text-slate-500 text-center py-6">No clients yet.</div>
            )}
            {rows.map((c) => (
              <div key={c.id} className="flex items-center gap-2 px-3 py-2"
                data-testid={`invoice-mgr-row-${c.id}`}>
                <span className="flex-1 text-sm truncate">{c.name}</span>
                <Button variant="ghost" size="sm"
                  onClick={() => setDeleteTarget(c)}
                  className="h-7 text-xs text-red-300 hover:text-red-200 hover:bg-red-500/10"
                  data-testid={`invoice-mgr-delete-${c.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[#0f1626] border-slate-800 text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              This also marks all roster workers under this client as inactive. Historical invoice + attendance data is preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-slate-700">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-500" onClick={confirmDelete}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
